import idxdDB from '@/assets/libs/IndexedDBController'
import ProductController from '@/assets/controllers/ProductController'
import CartController from '@/assets/controllers/CartController'
import UpdaterController from '@/assets/controllers/UpdaterController'

let suggestionsGroupsRaw = {}
let suggestionsProductsRaw = {}

let suggestionsGroups = []
let suggestionsByPriority = [] // Sugestões agrupadas por prioridade
let currentIndexCart = null
let currentGroupIndex = 0
let currentPriorityIndex = 0 // Rastreia o grupo de prioridade atual
let currentGroupCode = null
let currentProductCode = null

let products = []
let selectedSuggestedGroups = []
let selectedSuggestionKeys = [] // Rastreia chaves únicas de sugestões (groupCode||question)
let ignoreAllSuggestions = false
let lastIndexCart = null
let lastShownIndexCart = -1
let suggestionsLoaded = false
let loadSuggestionsPromise = null
let pendingGroupSuggestion = null

const normalizeCode = function (value) {
    return String(value || '').trim()
}

const normalizeProductCodeList = function (productCode) {
    if (Array.isArray(productCode)) {
        return productCode.map(code => normalizeCode(code)).filter(code => code.length > 0)
    }
    if (productCode === null || productCode === undefined || productCode === '') {
        return []
    }
    return [normalizeCode(productCode)].filter(code => code.length > 0)
}

const buildProductKey = function (groupCode, productCode) {
    return `${normalizeCode(groupCode)}|${normalizeCode(productCode)}`
}

const buildSuggestionKey = function (groupCode, question) {
    return `${normalizeCode(groupCode)}||${question || ''}`
}

const setSuggestionsRaw = function (suggestions) {
    suggestionsGroupsRaw = (suggestions && suggestions.groups) || {}
    suggestionsProductsRaw = (suggestions && suggestions.products) || {}
    suggestionsLoaded = true
}

const readSuggestionsFromDb = async function () {
    const suggestions = await idxdDB.getSuggestionsSIsFromDb()
    setSuggestionsRaw(suggestions)
    return suggestions
}

const ensureSuggestionsLoaded = async function (force = false) {
    if (suggestionsLoaded && !force) {
        return true
    }

    if (loadSuggestionsPromise && !force) {
        return loadSuggestionsPromise
    }

    loadSuggestionsPromise = (async () => {
        if (force) {
            suggestionsLoaded = false
            suggestionsGroupsRaw = {}
            suggestionsProductsRaw = {}
        }

        let suggestions = await readSuggestionsFromDb()

        const hasSuggestions =
            Object.keys((suggestions && suggestions.groups) || {}).length > 0 ||
            Object.keys((suggestions && suggestions.products) || {}).length > 0

        if (!hasSuggestions) {
            await UpdaterController.updateNewSuggestions(true)
            suggestions = await readSuggestionsFromDb()
        }

        return true
    })().finally(() => {
        loadSuggestionsPromise = null
    })

    return loadSuggestionsPromise
}

const markSuggestionsShown = function (indexCart) {
    lastShownIndexCart = indexCart
}

const hasActiveSuggestionFlow = function (indexCart) {
    // Só considera fluxo ativo se pertencer ao mesmo item do carrinho
    if (currentIndexCart === null || currentIndexCart !== indexCart) {
        return false
    }
    return suggestionsByPriority.length > 0 && currentPriorityIndex < suggestionsByPriority.length
}

const hasAnyActiveSuggestionFlow = function () {
    // Verifica se há qualquer fluxo ativo OU concluído, independente do índice
    // Um fluxo concluído ainda deve bloquear novos fluxos até ser explicitamente resetado
    return currentIndexCart !== null && suggestionsByPriority.length > 0
}

const setPendingGroupSuggestion = function (groupCode, question) {
    pendingGroupSuggestion = { groupCode, question, cartSizeAtTime: CartController.getCart().length }
}

const getPendingGroupSuggestion = function () {
    return pendingGroupSuggestion
}

const clearPendingGroupSuggestion = function () {
    pendingGroupSuggestion = null
}

const confirmPendingGroupSuggestion = function (productGroupCode) {
    if (!pendingGroupSuggestion) return false

    const currentCartSize = CartController.getCart().length
    if (currentCartSize <= pendingGroupSuggestion.cartSizeAtTime) {
        return 'no_new_product'
    }

    const normalizedProductGroup = normalizeCode(productGroupCode)
    const normalizedPendingGroup = normalizeCode(pendingGroupSuggestion.groupCode)
    const isMatch = normalizedProductGroup === normalizedPendingGroup

    setProductGroupSuggested(pendingGroupSuggestion.groupCode, pendingGroupSuggestion.question)
    clearPendingGroupSuggestion()
    return isMatch ? 'match' : 'no_match'
}

const isFlowCompleted = function () {
    // Verifica se o fluxo de sugestões foi completamente concluído
    return currentIndexCart !== null &&
        suggestionsByPriority.length > 0 &&
        currentPriorityIndex >= suggestionsByPriority.length
}

const buildSuggestionGroup = function (group, key) {
    const normalizedGroupCode = normalizeCode(group.groupCode)
    const groupCategory = products.find(product => normalizeCode(product.groupCode) === normalizedGroupCode)

    if (!groupCategory) return null

    let groupProducts = groupCategory.products || []
    const allowedProducts = normalizeProductCodeList(group.productCode)

    if (allowedProducts.length > 0) {
        groupProducts = groupProducts.filter(product => allowedProducts.includes(normalizeCode(product.productCode)))
    }

    if (!groupProducts || groupProducts.length === 0) return null

    const rawPriority = group.priority
    const parsedPriority = (rawPriority !== null && rawPriority !== undefined) ? Number(rawPriority) : NaN
    const priority = Number.isFinite(parsedPriority) ? parsedPriority : 999

    return {
        groupCode: normalizedGroupCode,
        groupName: groupCategory.groupName || '',
        groupImages: groupCategory.groupImages || null,
        question: group.question,
        priority,
        products: groupProducts,
        isGroupSuggestion: allowedProducts.length === 0,
        open: false
    }
}

const getSuggestions = async function (groupCode, productCode, indexCart) {
    const normalizedGroupCode = normalizeCode(groupCode)
    const normalizedProductCode = normalizeCode(productCode)

    // Se tem fluxo ativo para o mesmo item, retorna as sugestões atuais
    if (hasActiveSuggestionFlow(indexCart)) {
        return suggestionsGroups
    }

    // Se o fluxo foi completado e é um novo índice, verifica se é produto não sugerido
    if (isFlowCompleted() && indexCart !== currentIndexCart) {
        const cart = CartController.getCart()
        const cartItem = cart[indexCart]
        // Só reseta se for um produto não sugerido
        if (cartItem && !cartItem.productSuggested) {
            resetSuggestionsController()
            // Após reset, hasAnyActiveSuggestionFlow() retorna false e continua
        } else {
            // É um produto sugerido, não deve buscar suas sugestões
            return []
        }
    }

    // Se há qualquer fluxo ativo (não completado), bloqueia
    if (hasAnyActiveSuggestionFlow()) {
        return []
    }

    await ensureSuggestionsLoaded()
    products = await ProductController.getProducts()

    const productKey = buildProductKey(normalizedGroupCode, normalizedProductCode)
    const suggestionsByProduct = suggestionsProductsRaw[productKey] || []
    const suggestionsByGroup = suggestionsGroupsRaw[normalizedGroupCode] || []

    suggestionsGroups = []

    const seenSuggestionKeys = new Set()

    if (suggestionsByProduct.length > 0) {
        suggestionsByProduct.forEach((group, key) => {
            const dedupKey = buildSuggestionKey(group.groupCode, group.question)
            if (seenSuggestionKeys.has(dedupKey)) return
            seenSuggestionKeys.add(dedupKey)
            const suggestion = buildSuggestionGroup(group, key)
            if (suggestion) suggestionsGroups.push(suggestion)
        })
    } else {
        suggestionsByGroup.forEach((group, key) => {
            const dedupKey = buildSuggestionKey(group.groupCode, group.question)
            if (seenSuggestionKeys.has(dedupKey)) return
            seenSuggestionKeys.add(dedupKey)
            const suggestion = buildSuggestionGroup(group, key)
            if (suggestion) suggestionsGroups.push(suggestion)
        })
    }

    mergeSuggestionGroups()
    orderSuggestionsByPriority()
    groupSuggestionsByPriority()

    if (suggestionsGroups.length > 0) {
        suggestionsGroups[0].open = true
        currentIndexCart = indexCart
        currentGroupIndex = 0
        currentPriorityIndex = 0
        currentGroupCode = normalizedGroupCode
        currentProductCode = normalizedProductCode
    }

    return suggestionsGroups
}

const mergeSuggestionGroups = function () {
    const mergeMap = new Map()
    suggestionsGroups.forEach(suggestion => {
        const key = `${suggestion.groupCode}||${suggestion.question || ''}`
        if (mergeMap.has(key)) {
            const existing = mergeMap.get(key)
            suggestion.products.forEach(p => {
                const pCode = normalizeCode(p.productCode)
                if (!existing.products.find(ep => normalizeCode(ep.productCode) === pCode)) {
                    existing.products.push(p)
                }
            })
            if (suggestion.priority < existing.priority) {
                existing.priority = suggestion.priority
            }
        } else {
            mergeMap.set(key, { ...suggestion, products: [...suggestion.products] })
        }
    })
    suggestionsGroups = Array.from(mergeMap.values())
}

const orderSuggestionsByPriority = function () {
    if (suggestionsGroups.length === 0) {
        return
    }
    suggestionsGroups.sort((a, b) => b.priority - a.priority)
}

const groupSuggestionsByPriority = function () {
    if (!suggestionsGroups || suggestionsGroups.length === 0) {
        suggestionsByPriority = []
        return
    }

    // 1. Cria uma cópia e ordena por prioridade de forma crescente
    const sortedGroups = [...suggestionsGroups].sort((a, b) => b.priority - a.priority)

    let nextAvailablePriority = 0

    // 2. Ajusta as prioridades duplicadas e mapeia para o formato final
    suggestionsByPriority = sortedGroups.map((suggestion, index) => {
        let currentPriority = suggestion.priority

        if (index === 0) {
            // Define a primeira prioridade base para os próximos elementos
            nextAvailablePriority = currentPriority + 1
        } else {
            // Se a prioridade atual conflitar (for menor ou igual à esperada), ajustamos ela
            if (currentPriority < nextAvailablePriority) {
                currentPriority = nextAvailablePriority
            }
            // Atualiza qual deve ser a próxima prioridade livre
            nextAvailablePriority = currentPriority + 1
        }

        return {
            priority: currentPriority,
            suggestions: [suggestion]
        }
    })
}

const setProductGroupSuggested = function (groupCode, question = null) {
    const normalizedGroupCode = normalizeCode(groupCode)
    if (normalizedGroupCode) {
        selectedSuggestedGroups.push(normalizedGroupCode)
    }

    // Se a pergunta não foi fornecida, busca nas sugestões da prioridade atual
    let suggestionQuestion = question
    if (!suggestionQuestion && suggestionsByPriority.length > 0 && currentPriorityIndex < suggestionsByPriority.length) {
        const currentPriorityGroup = suggestionsByPriority[currentPriorityIndex]
        const matchingSuggestion = currentPriorityGroup.suggestions.find(s =>
            normalizeCode(s.groupCode) === normalizedGroupCode
        )
        if (matchingSuggestion) {
            suggestionQuestion = matchingSuggestion.question
        }
    }

    // Rastreia a chave única da sugestão
    const suggestionKey = buildSuggestionKey(groupCode, suggestionQuestion)
    if (!selectedSuggestionKeys.includes(suggestionKey)) {
        selectedSuggestionKeys.push(suggestionKey)
    }

    // Quando o usuário seleciona QUALQUER item da prioridade atual, avança para a próxima prioridade
    if (suggestionsByPriority.length > 0 && currentPriorityIndex < suggestionsByPriority.length) {
        const currentPriorityGroup = suggestionsByPriority[currentPriorityIndex]
        currentPriorityGroup.suggestions.forEach(s => {
            const key = buildSuggestionKey(s.groupCode, s.question)
            if (!selectedSuggestionKeys.includes(key)) {
                selectedSuggestionKeys.push(key)
            }
        })

        // Avança para a próxima prioridade
        currentPriorityIndex++

        // Atualiza currentGroupIndex para apontar para o primeiro item da próxima prioridade
        if (currentPriorityIndex < suggestionsByPriority.length) {
            const nextPriorityGroup = suggestionsByPriority[currentPriorityIndex]
            const firstSuggestionInNextPriority = nextPriorityGroup.suggestions[0]
            currentGroupIndex = suggestionsGroups.findIndex(s =>
                normalizeCode(s.groupCode) === normalizeCode(firstSuggestionInNextPriority.groupCode) &&
                s.question === firstSuggestionInNextPriority.question
            )
        } else {
            currentGroupIndex = suggestionsGroups.length
        }
    }

    // Se chegou ao fim das sugestões, marca como mostrado e permite novo fluxo para próximo produto
    if (currentPriorityIndex >= suggestionsByPriority.length && currentIndexCart !== null) {
        markSuggestionsShown(currentIndexCart)
        ignoreAllSuggestions = true
        // Não reseta currentIndexCart aqui para permitir verificação de fluxo completo
        // O reset será feito quando um novo produto não sugerido for adicionado
    }
}

const setNextGroupVisible = function (index) {
    let nextIndex = index + 1
    if (nextIndex < suggestionsGroups.length) {
        suggestionsGroups[index].open = false
        suggestionsGroups[nextIndex].open = true
    }
}

const getCurrentGroupIndex = function () {
    return currentGroupIndex
}

const getCurrentPriorityIndex = function () {
    return currentPriorityIndex
}

const getCurrentPrioritySuggestions = function () {
    if (suggestionsByPriority.length === 0 || currentPriorityIndex >= suggestionsByPriority.length) {
        return []
    }
    // Filtra sugestões que já foram selecionadas
    const allSuggestionsInPriority = suggestionsByPriority[currentPriorityIndex].suggestions
    return allSuggestionsInPriority.filter(s => {
        const key = buildSuggestionKey(s.groupCode, s.question)
        return !selectedSuggestionKeys.includes(key)
    })
}

const setIgnoreAllSuggestions = function (ignore) {
    ignoreAllSuggestions = ignore
}

const checkIfIgnoreAllSuggestions = function () {
    checkLastIndexCart()

    // Se o fluxo foi completado e há um novo produto não sugerido no carrinho (índice diferente),
    // permite que novas sugestões sejam exibidas
    if (isFlowCompleted() && lastIndexCart !== currentIndexCart) {
        return false
    }

    if (lastShownIndexCart !== -1 && lastShownIndexCart === lastIndexCart) return true
    if (ignoreAllSuggestions && (lastIndexCart === currentIndexCart)) return true
    return false
}

const checkLastIndexCart = function () {
    let cart = CartController.getCart()
    if (cart.length === 0) {
        return false
    }
    let lastIndexNotSuggested = cart.findLastIndex(item => !item.productSuggested)
    if (lastIndexNotSuggested === -1) {
        return false
    }
    lastIndexCart = lastIndexNotSuggested
    return true
}

const hasSuggestions = async function (groupCode, productCode) {
    await ensureSuggestionsLoaded()
    const normalizedGroupCode = normalizeCode(groupCode)
    const normalizedProductCode = normalizeCode(productCode)
    const productKey = buildProductKey(normalizedGroupCode, normalizedProductCode)

    if (suggestionsProductsRaw[productKey] && suggestionsProductsRaw[productKey].length > 0) return true
    if (suggestionsGroupsRaw[normalizedGroupCode] && suggestionsGroupsRaw[normalizedGroupCode].length > 0) return true
    return false
}

const resetSuggestionsController = function () {
    selectedSuggestedGroups = []
    selectedSuggestionKeys = []
    ignoreAllSuggestions = false
    lastIndexCart = null
    lastShownIndexCart = -1
    currentIndexCart = null
    suggestionsGroups = []
    suggestionsByPriority = []
    currentGroupIndex = 0
    currentPriorityIndex = 0
    currentGroupCode = null
    currentProductCode = null
    pendingGroupSuggestion = null
}

const resetCurrentGroupIndex = function () {
    currentGroupIndex = 0
    currentPriorityIndex = 0
}

const cancelAllSuggestions = function (indexCart) {
    // Marca como ignorar todas as sugestões para este item do carrinho
    setIgnoreAllSuggestions(true)
    markSuggestionsShown(indexCart)
    // Reseta o estado do fluxo para que próximas sugestões comecem do zero
    selectedSuggestedGroups = []
    selectedSuggestionKeys = []
    currentIndexCart = null
    suggestionsGroups = []
    suggestionsByPriority = []
    currentGroupIndex = 0
    currentPriorityIndex = 0
    currentGroupCode = null
    currentProductCode = null
}

const getActiveFlowContext = function () {
    if (currentIndexCart === null || suggestionsByPriority.length === 0 || currentPriorityIndex >= suggestionsByPriority.length) {
        return null
    }

    // Retorna o groupCode e productCode originais que iniciaram o fluxo
    return {
        indexCart: currentIndexCart,
        groupCode: currentGroupCode,
        productCode: currentProductCode
    }
}

export default {
    getSuggestions,
    hasSuggestions,
    setProductGroupSuggested,
    setNextGroupVisible,
    setIgnoreAllSuggestions,
    checkIfIgnoreAllSuggestions,
    getCurrentGroupIndex,
    getCurrentPriorityIndex,
    getCurrentPrioritySuggestions,
    resetSuggestionsController,
    resetCurrentGroupIndex,
    markSuggestionsShown,
    getActiveFlowContext,
    cancelAllSuggestions,
    setPendingGroupSuggestion,
    getPendingGroupSuggestion,
    clearPendingGroupSuggestion,
    confirmPendingGroupSuggestion
}



// WEBPACK FOOTER //
// ./src/assets/controllers/SuggestionsController.js